import React from 'react';
import { Input, type InputRef } from 'antd';
import { faxNanpDigitsForInput } from 'utils/eFaxScheduleRows';
import { buildNanpDisplayMask } from 'components/form/USFaxNumberInput';
import s from './s.module.less';

/** True when the box should use NANP masking. Empty string is false so the first character can be a letter (name search). */
export function isRecipientQueryFaxLike(raw: string): boolean {
    if (raw == null || raw === '') return false;
    if (/[a-zA-Z]/.test(raw)) return false;
    return /^[\d\s+().\-]*$/.test(raw);
}

type Props = React.ComponentProps<typeof Input>;

/**
 * Send-fax recipient field: allows typing a contact name for search, or a US fax number with
 * (555) 123-4567-style masking when input is digit-like only.
 */
const EFaxRecipientQueryInput = React.forwardRef<InputRef, Props>(function EFaxRecipientQueryInput(
    { value, onChange, size = 'middle', className, style, inputMode, autoComplete, ...rest },
    ref,
) {
    const str = value == null ? '' : String(value);
    const faxLike = isRecipientQueryFaxLike(str);
    const digits = faxNanpDigitsForInput(str);
    const inputValue = faxLike ? digits : str;
    const display = faxLike ? buildNanpDisplayMask(digits) : '';

    let paddingLeft: number | undefined;
    if (faxLike) {
        paddingLeft = 10;
        if (digits.length > 3) paddingLeft = 20;
        if (digits.length > 7) paddingLeft = size === 'large' ? 33 : 30;
    }

    let displayClass = s.sizeNormal;
    if (size === 'large') displayClass = s.sizeLarge;
    if (size === 'small') displayClass = s.sizeSmall;

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (!onChange) return;
        const raw = e.target.value;
        if (isRecipientQueryFaxLike(raw)) {
            const d = raw.replaceAll(/\D/g, '').slice(0, 10);
            const synthetic = {
                ...e,
                target: { ...e.target, value: d },
                currentTarget: { ...e.currentTarget, value: d },
            } as React.ChangeEvent<HTMLInputElement>;
            (onChange as React.ChangeEventHandler<HTMLInputElement>)(synthetic);
        } else {
            onChange(e);
        }
    };

    return (
        <div
            className={s.wrap}
            style={
                {
                    '--margin-left': '0px',
                } as React.CSSProperties
            }
        >
            <Input
                ref={ref}
                size={size}
                {...rest}
                value={inputValue}
                onChange={handleChange}
                className={`${faxLike ? s.maskedInput : ''} ${className || ''}`.trim()}
                style={
                    faxLike
                        ? {
                              ...style,
                              paddingLeft: `${paddingLeft}px`,
                          }
                        : style
                }
                inputMode={faxLike ? 'numeric' : inputMode}
                autoComplete={faxLike ? 'off' : autoComplete}
            />
            {faxLike ? <div className={`${s.display} ${displayClass}`}>{display}</div> : null}
        </div>
    );
});

EFaxRecipientQueryInput.displayName = 'EFaxRecipientQueryInput';

export default EFaxRecipientQueryInput;
